import { Portfolio, Site } from "../../sites/sites.module";
import type { CommodityFixings } from "../PricingRequestStepper.slice";

export interface PricingSectionDefault {
	index: number;
	threshold: number;
	operator: "<" | "<=";
	nb_consecutive_blocks: number;
}

export type PricingStepperDefaults = Record<
	string,
	Record<
		string,
		Partial<Record<"curtailability" | "negative_price", PricingSectionDefault>>
	>
>;

export interface ResolvedSectionDefault {
	status: "empty" | "compatible" | "multiple" | "incompatible";
	value?: Omit<PricingSectionDefault, "index"> & { index: number };
}

export type ResolvedStepperDefaults = Record<
	"curtailability" | "negative_price",
	ResolvedSectionDefault
>;

function normalizeKey(value: string): string {
	return value.trim().toUpperCase();
}

function getMatchingKey<T>(
	record: Record<string, T>,
	key: string
): T | undefined {
	const normalizedKey = normalizeKey(key);
	const matchingKey = Object.keys(record).find(
		(candidate) => normalizeKey(candidate) === normalizedKey
	);
	return matchingKey === undefined ? undefined : record[matchingKey];
}

export function getPricingCountries(items: (Site | Portfolio)[]): string[] {
	return Array.from(
		new Set(
			items.flatMap((item) => {
				if (!("sites" in item)) {
					return [
						normalizeKey(item.country_iso2_code || item.country),
					];
				}
				const sites = item.sites?.length ? item.sites : item.children;
				return getPricingCountries(sites);
			})
		)
	);
}

function resolveTypeCountryDefault(
	defaults: PricingStepperDefaults,
	type: Pick<CommodityFixings, "business_key">,
	country: string,
	section: keyof ResolvedStepperDefaults
): ResolvedSectionDefault["value"] | undefined {
	if (!type.business_key) return undefined;
	const value = getMatchingKey(
		getMatchingKey(defaults, country) ?? {},
		type.business_key
	)?.[section];
	if (!value) return undefined;
	return value;
}

function sameDefault(
	first: NonNullable<ResolvedSectionDefault["value"]>,
	second: NonNullable<ResolvedSectionDefault["value"]>
): boolean {
	return (
		first.index === second.index &&
		first.threshold === second.threshold &&
		first.operator === second.operator &&
		first.nb_consecutive_blocks === second.nb_consecutive_blocks
	);
}

export function resolveSectionDefault(
	defaults: PricingStepperDefaults,
	pricingTypes: Pick<CommodityFixings, "business_key">[],
	countries: string[],
	section: keyof ResolvedStepperDefaults
): ResolvedSectionDefault {
	const combinations = pricingTypes.flatMap((type) =>
		countries.map((country) =>
			resolveTypeCountryDefault(defaults, type, country, section)
		)
	);
	const available = combinations.filter(
		(option): option is NonNullable<typeof option> => option !== undefined
	);
	const first = available[0];
	if (!first) return { status: "empty" };
	if (available.length !== combinations.length) {
		return { status: "incompatible", value: first };
	}
	const multiple = available.some((option) => !sameDefault(option, first));
	return { status: multiple ? "multiple" : "compatible", value: first };
}
